import { useEffect, useState } from 'react';
import { getAdminReadings } from '../../lib/api';
import type { AdminReadingFilters, AdminReadingListItem } from '../../lib/types';
import { AdminBadge, AdminEmptyState, AdminPanel, AdminReadingCard } from './AdminUi';

interface FilterFormState {
  query: string;
  userEmail: string;
  startDate: string;
  endDate: string;
  topic: string;
  spread: string;
  paymentStatus: string;
  generationStatus: string;
  source: string;
}

const defaultForm: FilterFormState = {
  query: '',
  userEmail: '',
  startDate: '',
  endDate: '',
  topic: '',
  spread: '',
  paymentStatus: '',
  generationStatus: '',
  source: '',
};

function formToFilters(form: FilterFormState): AdminReadingFilters {
  return {
    query: form.query || undefined,
    userEmail: form.userEmail || undefined,
    startDate: form.startDate || undefined,
    endDate: form.endDate || undefined,
    topic: (form.topic || undefined) as AdminReadingFilters['topic'],
    spread: (form.spread || undefined) as AdminReadingFilters['spread'],
    paymentStatus: (form.paymentStatus || undefined) as AdminReadingFilters['paymentStatus'],
    generationStatus: (form.generationStatus || undefined) as AdminReadingFilters['generationStatus'],
    source: (form.source || undefined) as AdminReadingFilters['source'],
    limit: 100,
  };
}

export default function AdminReadingsPage() {
  const [form, setForm] = useState<FilterFormState>(defaultForm);
  const [activeFilters, setActiveFilters] = useState<AdminReadingFilters>({ limit: 100 });
  const [items, setItems] = useState<AdminReadingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await getAdminReadings(activeFilters);
        if (!disposed) {
          setItems(response.readings);
          setErrorMessage(null);
        }
      } catch (error) {
        if (!disposed) {
          const apiError = error as Error;
          setErrorMessage(apiError.message || '관리자 리딩 목록을 불러오지 못했습니다.');
        }
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      disposed = true;
    };
  }, [activeFilters]);

  const updateField = (key: keyof FilterFormState, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setActiveFilters(formToFilters(form));
  };

  const handleReset = () => {
    setForm(defaultForm);
    setActiveFilters({ limit: 100 });
  };

  return (
    <div className="space-y-6">
      <AdminPanel
        title="리딩 관리"
        subtitle="리딩 ID, 거래 ID, 이메일, 스프레드, 결제 상태, LLM 출처로 검색합니다"
        actions={<AdminBadge label={`${items.length}건`} tone="accent" />}
      >
        <form className="grid gap-3 md:grid-cols-2 xl:grid-cols-5" onSubmit={handleSubmit}>
          <input
            value={form.query}
            onChange={(event) => updateField('query', event.target.value)}
            placeholder="리딩 ID / 거래 ID / 이메일"
            className="border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
          />
          <input
            value={form.userEmail}
            onChange={(event) => updateField('userEmail', event.target.value)}
            placeholder="사용자 이메일"
            className="border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
          />
          <input
            type="date"
            value={form.startDate}
            onChange={(event) => updateField('startDate', event.target.value)}
            className="border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
          />
          <input
            type="date"
            value={form.endDate}
            onChange={(event) => updateField('endDate', event.target.value)}
            className="border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
          />
          <select
            value={form.topic}
            onChange={(event) => updateField('topic', event.target.value)}
            className="border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
          >
            <option value="">모든 주제</option>
            <option value="연애">연애</option>
            <option value="직장">직장</option>
            <option value="금전">금전</option>
            <option value="종합">종합</option>
          </select>
          <select
            value={form.spread}
            onChange={(event) => updateField('spread', event.target.value)}
            className="border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
          >
            <option value="">모든 스프레드</option>
            <option value="one-card">원카드</option>
            <option value="three-card">쓰리카드</option>
            <option value="celtic-cross">켈틱 크로스</option>
          </select>
          <select
            value={form.paymentStatus}
            onChange={(event) => updateField('paymentStatus', event.target.value)}
            className="border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
          >
            <option value="">모든 결제 상태</option>
            <option value="pending">대기 중</option>
            <option value="paid">결제 완료</option>
            <option value="failed">결제 실패</option>
          </select>
          <select
            value={form.generationStatus}
            onChange={(event) => updateField('generationStatus', event.target.value)}
            className="border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
          >
            <option value="">모든 생성 상태</option>
            <option value="pending">대기 중</option>
            <option value="generating">생성 중</option>
            <option value="completed">생성 완료</option>
            <option value="failed">생성 실패</option>
          </select>
          <select
            value={form.source}
            onChange={(event) => updateField('source', event.target.value)}
            className="border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
          >
            <option value="">모든 응답 출처</option>
            <option value="live">실응답</option>
            <option value="fallback">폴백</option>
            <option value="unknown">미확인</option>
          </select>

          <div className="flex gap-3 xl:col-span-2">
            <button
              type="submit"
              className="flex-1 border border-[#8a6a4b]/45 bg-[#241913] px-4 py-3 text-sm font-medium text-[#f3dfc5] hover:bg-[#2d2019]"
            >
              필터 적용
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="border border-[#8a6a4b]/25 bg-[#17110f] px-4 py-3 text-sm text-[#d8bfa1]/80 hover:bg-[#1f1511]"
            >
              초기화
            </button>
          </div>
        </form>
      </AdminPanel>

      <AdminPanel title="리딩 목록" subtitle="최신 순으로 정렬됩니다">
        {loading ? (
          <p className="text-sm text-[#d8bfa1]/75">리딩 목록을 불러오고 있습니다...</p>
        ) : errorMessage ? (
          <AdminEmptyState title="리딩 목록을 불러올 수 없습니다" body={errorMessage} />
        ) : items.length === 0 ? (
          <AdminEmptyState title="조건에 맞는 리딩이 없습니다" body="필터를 바꾸거나 메인 플로우에서 새 리딩을 생성해 보세요." />
        ) : (
          <div className="space-y-3">
            {items.map((reading) => (
              <AdminReadingCard
                key={reading.id}
                reading={reading}
                to={`/admin/readings/${reading.id}`}
              />
            ))}
          </div>
        )}
      </AdminPanel>
    </div>
  );
}
